/**
 * LegislatorFilters Component
 * 
 * Shared filter UI for legislators across all contexts.
 * Configurable to show/hide specific filters based on context.
 */

import React from 'react';
import { Search } from 'lucide-react';
import SearchField from '../SearchField';
import Button from '../Button';
import FilterActions from '../FilterActions';
import {
  LegislatorFilterState,
  LegislatorFilterOptions,
  LegislatorFiltersConfig,
  DEFAULT_FILTERS_CONFIG,
  Committee,
} from './types';
import { useSessionOptions, useSessions, CURRENT_SESSION } from '../../hooks/useSessions';
import { useIncomingRoster } from './incomingRoster';
import { HelpTrigger } from '../../help';

/**
 * Format committee name with chamber prefix for display (matches bill filters)
 * @example "H-Agriculture" for House, "S-Finance" for Senate
 */
function formatCommitteeDisplayName(committee: Committee): string {
  const chamber = committee.chamber?.toLowerCase();
  if (chamber === 'house') {
    return `H-${committee.name}`;
  } else if (chamber === 'senate') {
    return `S-${committee.name}`;
  }
  return committee.name; // Fallback if no chamber info
}

interface LegislatorFiltersProps {
  /** Current filter state */
  filters: LegislatorFilterState;
  /** Filter options from API */
  filterOptions: LegislatorFilterOptions;
  /** Callback when a filter changes */
  onFilterChange: (key: keyof LegislatorFilterState, value: string) => void;
  /** Callback to clear all filters */
  onClearFilters: () => void;
  /** Callback for search button click (optional) */
  onSearch?: () => void;
  /** Configuration for which filters to show */
  config?: LegislatorFiltersConfig;
  /** Show as card with padding */
  showCard?: boolean;
  /** Custom class name */
  className?: string;
  /** Loading state for filter options */
  isLoadingOptions?: boolean;
}

const LegislatorFilters: React.FC<LegislatorFiltersProps> = ({
  filters,
  filterOptions,
  onFilterChange,
  onClearFilters,
  onSearch,
  config = DEFAULT_FILTERS_CONFIG,
  showCard = true,
  className = '',
  isLoadingOptions = false,
}) => {
  const {
    showSearch = true,
    showSessionYear = true,
    showChamber = true,
    showCommittee = true,
    showParty = true,
    showDistrict = true,
    showClearButton = true,
    showSearchButton = true,
    compact = false,
  } = config;

  const handleInputChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
    key: keyof LegislatorFilterState
  ) => {
    onFilterChange(key, e.target.value);
  };

  // Determine grid columns based on which filters are shown
  const getGridCols = () => {
    const visibleFilters = [
      showSessionYear,
      showChamber,
      showCommittee,
      showParty,
      showDistrict,
    ].filter(Boolean).length;

    if (compact) return 'grid-cols-2 lg:grid-cols-4';
    if (visibleFilters <= 2) return 'grid-cols-1 sm:grid-cols-2';
    if (visibleFilters <= 3) return 'grid-cols-1 sm:grid-cols-3';
    if (visibleFilters <= 4) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
    return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5';
  };

  const { options: sessionOptions, isLoading: sessionsLoading } = useSessionOptions();
  const { currentSessionKey } = useSessions();
  // The next session's pre-built roster of members-elect (ELECTION_OUTCOMES_PRD §5.14).
  const { data: incoming } = useIncomingRoster();
  /*
    The stored default is the CURRENT_SESSION sentinel (resolved server-side so
    the page never freezes on a wall-clock year). The <select> has no 'current'
    option — an unmatched value made it silently display its first option, "All
    Sessions", while the list was actually scoped to the current session. Show
    the sentinel as the real current session instead. 'All' is this page's
    legacy unscoped spelling, mapped to the registry option's 'all'.
  */
  const sessionSelectValue =
    filters.sessionYear === CURRENT_SESSION
      ? currentSessionKey ?? 'all'
      : filters.sessionYear === 'All'
        ? 'all'
        : filters.sessionYear;

  const content = (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {/* Row 1: Filters Grid */}
      <div className={`grid ${getGridCols()} gap-4`}>
        {/* Session filter — options come from the registry, so every registered
            session is reachable (this was a hardcoded 2-year list). */}
        {showSessionYear && (
          <div>
            <label className="flex items-center gap-1 text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
              Session
              <HelpTrigger contentKey="session-scope" size="sm" />
            </label>
            <select
              value={sessionSelectValue}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleInputChange(e, 'sessionYear')}
              className="input"
              disabled={sessionsLoading}
            >
              {sessionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.value === 'all' ? 'All Sessions'
                    : opt.value === incoming?.sessionKey ? `${incoming.shortLabel} (incoming)` : opt.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Chamber Filter */}
        {showChamber && (
          <div>
            <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
              Chamber
            </label>
            <select
              value={filters.chamber}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleInputChange(e, 'chamber')}
              className="input"
              disabled={isLoadingOptions}
            >
              <option value="All">All Chambers</option>
              {filterOptions.chambers.map((chamber) => (
                <option key={chamber} value={chamber}>{chamber}</option>
              ))}
            </select>
          </div>
        )}

        {/* Committee Filter */}
        {showCommittee && (
          <div>
            <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
              Committee
            </label>
            <select
              value={filters.committee}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleInputChange(e, 'committee')}
              className="input"
              disabled={isLoadingOptions}
            >
              <option value="All committees">All Committees</option>
              {filterOptions.committees.map((committee) => (
                <option key={`${committee.name}-${committee.chamber}`} value={`${committee.name} (${committee.chamber})`}>
                  {formatCommitteeDisplayName(committee)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Party Filter */}
        {showParty && (
          <div>
            <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
              Party
            </label>
            <select
              value={filters.party}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleInputChange(e, 'party')}
              className="input"
              disabled={isLoadingOptions}
            >
              <option value="All parties">All Parties</option>
              {filterOptions.parties.map((party) => (
                <option key={party} value={party}>{party}</option>
              ))}
            </select>
          </div>
        )}

        {/* District Filter */}
        {showDistrict && (
          <div>
            <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
              District
            </label>
            <select
              value={filters.district}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleInputChange(e, 'district')}
              className="input"
              disabled={isLoadingOptions}
            >
              <option value="All districts">All Districts</option>
              {filterOptions.districts.map((district) => (
                <option key={`${district.district}-${district.chamber}`} value={district.district}>
                  {district.displayName}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Row 2: Search Bar spanning 4 columns + Clear Filters right-justified */}
      {(showSearch || showClearButton || showSearchButton) && (
        <div className={`grid ${getGridCols()} gap-4 items-end`}>
          {/* Search field spans first 4 columns */}
          <div className="col-span-full lg:col-span-4">
            {showSearch && (
              <SearchField
                value={filters.searchQuery}
                onChange={(value) => onFilterChange('searchQuery', value)}
                placeholder="Search legislators by name or district..."
                className="w-full"
                size={compact ? 'sm' : 'md'}
              />
            )}
          </div>
          {/* Clear Filters button in 5th column (right-justified) */}
          {showClearButton && (
            <div className="col-span-full lg:col-span-1">
              <FilterActions onClear={onClearFilters} />
            </div>
          )}
          {showSearchButton && onSearch && (
            <div className="col-span-full lg:col-span-1 flex justify-end">
              <Button
                variant="primary"
                size="sm"
                icon={Search}
                onClick={onSearch}
              >
                Search
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (showCard) {
    return (
      <div className={`card ${className}`}>
        <div className={compact ? 'p-4' : 'p-6'}>
          {content}
        </div>
      </div>
    );
  }

  return <div className={className}>{content}</div>;
};

export default LegislatorFilters;
